import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Save, RotateCcw } from "lucide-react";
import { showSuccess, showApiError, getLocalStorageWithExpiry, clearAuthStorage } from "@/utils";
import apiClient from "@/services/apiClient";
import { ANSWER_ENGINE_VERSION_ID_KEY, CLIENT_ID_KEY } from "@/const";
import BrandButton from "@/components/ui/BrandButton";
import GlassSlider from "@/components/ui/GlassSlider";
import AgentSetting from "./AgentSetting";
import {
  WIDGET_SETTINGS_DEFAULTS as DEFAULTS,
  FONT_OPTIONS,
  PRESET_FONTS_HREF,
  loadWidgetSettings as loadSettings,
  persistWidgetSettings as persistSettings,
  isValidHex,
  mapApiToSettings,
  normalizeWidgetMode,
} from "./widgetSettings";
import { AE_BACKEND_URL } from "@/config";

export default function AnswerEngineSettings({ onDirtyChange }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(DEFAULTS);
  const [fontSelectValue, setFontSelectValue] = useState("");
  const [customFont, setCustomFont] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [isAgentDirty, setIsAgentDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSuperAdmin = getLocalStorageWithExpiry("role") === "super-admin";
  const clientId = getLocalStorageWithExpiry(CLIENT_ID_KEY);
  const seededFromApi = useRef(false);
  const lastSeededClientId = useRef(null);

  useEffect(() => {
    onDirtyChange?.(isDirty || isAgentDirty);
  }, [isDirty, isAgentDirty, onDirtyChange]);

  useEffect(() => {
    const isAnyDirty = isDirty || isAgentDirty;
    const handleBeforeUnload = (e) => {
      if (isAnyDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isAgentDirty]);

  const {
    data: remoteConfig,
    isLoading: isLoadingConfig,
    error: remoteConfigError,
  } = useQuery({
    queryKey: ["widget-config", clientId],
    queryFn: async () => {
      try {
        const resp = await apiClient.get(
          `${AE_BACKEND_URL}/admin/widget-config/${encodeURIComponent(clientId)}`,
        );
        return resp.data;
      } catch (err) {
        if (err.response?.status === 401) {
          clearAuthStorage();
          localStorage.setItem("redirectAfterLogin", window.location.pathname + window.location.search);
          window.location.assign("/login");
          throw new Error("Session expired. Redirecting to login...");
        }
        const errorMessage =
          err.response?.data?.message || err.message || `HTTP error! Status: ${err.response?.status}`;
        throw new Error(errorMessage);
      }
    },
    enabled: !!clientId && !!AE_BACKEND_URL,
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (remoteConfigError) {
      showApiError(
        remoteConfigError.message || "Failed to load saved settings from server.",
      );
    }
  }, [remoteConfigError]);

  useEffect(() => {
    if (!remoteConfig || (seededFromApi.current && lastSeededClientId.current === clientId)) return;
    seededFromApi.current = true;
    lastSeededClientId.current = clientId;
    const mapped = mapApiToSettings(remoteConfig);
    setSettings((prev) => ({
      ...prev,
      ...mapped,
      // preserve super-admin versionId from localStorage
      versionId: prev.versionId,
    }));
    const isCustom =
      mapped.modalTitleFont &&
      !FONT_OPTIONS.some((o) => o.value === mapped.modalTitleFont);
    if (isCustom) {
      setFontSelectValue("__custom__");
      setCustomFont(mapped.modalTitleFont);
    } else {
      setFontSelectValue(mapped.modalTitleFont || "");
    }
    setIsDirty(false);
  }, [remoteConfig, clientId]);

  // Lazy-load all preset Google Fonts — only fetched when this settings
  // page is visited, not on every app route.
  useEffect(() => {
    const LINK_ID = "ge-preset-fonts";
    if (!document.getElementById(LINK_ID)) {
      const link = document.createElement("link");
      link.id = LINK_ID;
      link.rel = "stylesheet";
      link.href = PRESET_FONTS_HREF;
      document.head.appendChild(link);
    }
    // No cleanup: fonts stay cached for the session so navigating back
    // to this page doesn't re-fetch them.
  }, []);

  useEffect(() => {
    const defaultVersionId =
      getLocalStorageWithExpiry(ANSWER_ENGINE_VERSION_ID_KEY) || "production";
    const loaded = loadSettings();
    const merged = {
      ...loaded,
      widgetMode: normalizeWidgetMode(loaded.widgetMode),
      versionId: loaded.versionId || defaultVersionId,
    };
    setSettings(merged);
    const isCustom =
      merged.modalTitleFont &&
      !FONT_OPTIONS.some((o) => o.value === merged.modalTitleFont);
    if (isCustom) {
      setFontSelectValue("__custom__");
      setCustomFont(merged.modalTitleFont);
    } else {
      setFontSelectValue(merged.modalTitleFont || "");
    }
  }, []);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleHexInput = (key, raw) => {
    const v = raw.startsWith("#") ? raw : "#" + raw;
    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) update(key, v);
  };

  const handleFontSelect = (value) => {
    setFontSelectValue(value);
    if (value !== "__custom__") {
      update("modalTitleFont", value);
    }
    setIsDirty(true);
  };

  const handleCustomFontChange = (value) => {
    setCustomFont(value);
    update("modalTitleFont", value);
  };

  const sanitizeSettings = (s) => ({
    ...s,
    accentColor: isValidHex(s.accentColor)
      ? s.accentColor
      : DEFAULTS.accentColor,
    chatThemeColor: isValidHex(s.chatThemeColor) ? s.chatThemeColor : "",
    // Non-super-admins cannot edit versionId; clear it so the Sandbox
    // always falls back to the login-provided default instead of a
    // stale persisted value.
    versionId: isSuperAdmin ? (s.versionId || "").trim() : "",
    bannerTitle: (s.bannerTitle || "").trim(),
    bannerDescription: (s.bannerDescription || "").trim(),
    bannerImageUrl: (s.bannerImageUrl || "").trim(),
    widgetMode: normalizeWidgetMode(s.widgetMode),
    suggestedQuestions: (Array.isArray(s.suggestedQuestions) ? s.suggestedQuestions : []).map((q) => (q ?? "").toString().trim()).filter(Boolean),
    hubspotPortalId: (s.hubspotPortalId || "").trim(),
    hubspotFormId: (s.hubspotFormId || "").trim(),
    hubspotRegion: (s.hubspotRegion || "").trim(),
  });

  const toWidgetConfigPayload = (sanitized, resolvedClientId) => {
    const toNum = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const num = Number(v);
      return isNaN(num) ? null : num;
    };
    return {
      accent_color: sanitized.accentColor || null,
      banner_description: sanitized.bannerDescription || null,
      banner_image_url: sanitized.bannerImageUrl || null,
      banner_title: sanitized.bannerTitle || null,
      blur: toNum(sanitized.glassBlur),
      calendly_url: sanitized.calendlyUrl || null,
      chat_opacity: toNum(sanitized.glassChatOpacity),
      client_id: resolvedClientId,
      cta_button_text: sanitized.bookingCtaButton || null,
      cta_title: sanitized.bookingCtaTitle || null,
      dialog_opacity: toNum(sanitized.glassDialogOpacity),
      hubspot_form_id: sanitized.hubspotFormId || null,
      hubspot_portal_id: sanitized.hubspotPortalId || null,
      hubspot_region: sanitized.hubspotRegion || null,
      modal_title: sanitized.modalTitle || null,
      scrim_opacity: toNum(sanitized.glassScrimOpacity),
      suggested_questions: sanitized.suggestedQuestions || [],
      title_font: sanitized.modalTitleFont || null,
      widget_mode: sanitized.widgetMode || null,
    };
  };

  const putWidgetConfig = async (sanitized) => {
    if (!clientId) return false;
    try {
      const body = toWidgetConfigPayload(sanitized, clientId);
      await apiClient.put(
        `${AE_BACKEND_URL}/admin/widget-config/${encodeURIComponent(clientId)}`,
        body,
      );
      return true;
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthStorage();
        localStorage.setItem("redirectAfterLogin", window.location.pathname + window.location.search);
        window.location.assign("/login");
        return false;
      }
      return false;
    }
  };

  const handleSave = async () => {
    if (!clientId) {
      showApiError("Missing client ID. Please log in again and try saving.");
      return;
    }

    const sanitized = sanitizeSettings(settings);

    setIsSaving(true);
    const savedRemotely = await putWidgetConfig(sanitized);
    setIsSaving(false);
    if (!savedRemotely) {
      showApiError("Failed to update widget config on server.");
      return;
    }

    setSettings(sanitized);
    const ok = persistSettings(sanitized);
    if (!ok) {
      showApiError(
        t(
          "answerEngine.settingsSaveError",
          "Failed to save settings. Storage may be full or unavailable.",
        ),
      );
      return;
    }

    setIsDirty(false);
    showSuccess(
      t(
        "answerEngine.settingsSavedSuccess",
        "Widget settings saved. Reload the Sandbox to apply changes.",
      ),
    );
  };

  const handleReset = async () => {
    if (!clientId) {
      showApiError("Missing client ID. Please log in again and try resetting.");
      return;
    }

    const reset = {
      ...DEFAULTS,
      versionId: isSuperAdmin
        ? getLocalStorageWithExpiry(ANSWER_ENGINE_VERSION_ID_KEY) ||
          "production"
        : "",
    };

    setIsSaving(true);
    const resetRemotely = await putWidgetConfig(reset);
    setIsSaving(false);
    if (!resetRemotely) {
      showApiError("Failed to update widget config on server.");
      return;
    }

    setSettings(reset);
    setFontSelectValue("");
    setCustomFont("");
    const ok = persistSettings(reset);
    if (!ok) {
      showApiError(
        t(
          "answerEngine.settingsResetError",
          "Failed to reset settings. Storage may be full or unavailable.",
        ),
      );
      return;
    }

    setIsDirty(false);
    showSuccess(
      t(
        "answerEngine.settingsResetSuccess",
        "Widget settings reset to defaults.",
      ),
    );
  };

  const resolvedAccent = isValidHex(settings.accentColor)
    ? settings.accentColor
    : "#2DC6FE";

  return (
    <div className="row g-4 align-items-start">
    <div className="col-12 col-md-6">
    <div className="card card-radius shadow-sm border-0 p-4">
      <h5 className="fw-bold text-dark mb-1">
        {t("answerEngine.widgetBranding", "Widget Branding")}
      </h5>
      <p className="text-muted small mb-4">
        {t(
          "answerEngine.widgetBrandingSubtitle",
          "Customize the appearance of the Answer Engine widget embedded in the Sandbox. Changes take effect after reloading the Sandbox tab.",
        )}
      </p>

      {isLoadingConfig && (
        <div className="d-flex align-items-center gap-2 text-muted small mb-4">
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          Loading saved settings…
        </div>
      )}

      {!isLoadingConfig && (<>

      {isSuperAdmin && (
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.versionId", "Version ID")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.versionIdDesc",
              "Selects which published widget version to load.",
            )}
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 320 }}
            placeholder="main"
            value={settings.versionId}
            onChange={(e) => update("versionId", e.target.value)}
          />
        </div>
      )}

      {/* ── Accent Color ─────────────────────────────────── */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark mb-1">
          {t("answerEngine.accentColor", "Accent Color")}
        </label>
        <p className="small text-muted mb-2">
          {t(
            "answerEngine.accentColorDesc",
            "Primary brand color used for buttons and highlights in the widget.",
          )}
        </p>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <input
            type="color"
            className="form-control form-control-color flex-shrink-0"
            value={resolvedAccent}
            onChange={(e) => update("accentColor", e.target.value)}
            style={{
              width: 48,
              height: 38,
              padding: "2px 4px",
              cursor: "pointer",
            }}
            title={t("answerEngine.accentColor", "Pick accent color")}
          />
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 140, fontFamily: "monospace" }}
            value={settings.accentColor}
            maxLength={7}
            placeholder="#2DC6FE"
            onChange={(e) =>
              handleHexInput(
                "accentColor",
                e.target.value.replace(/[^#0-9a-fA-F]/g, ""),
              )
            }
          />
          <span className="small text-muted">
            {t("common.default", "Default")}: #2DC6FE
          </span>
        </div>
      </div>

      {/* ── Modal Title ───────────────────────────────────── */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark mb-1">
          {t("answerEngine.modalTitle", "Modal Title")}
        </label>
        <p className="small text-muted mb-2">
          {t(
            "answerEngine.modalTitleDesc",
            "Title displayed at the top of the widget modal. Max 50 characters.",
          )}
        </p>
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 380 }}
          placeholder="elelem Answer Engine"
          maxLength={50}
          value={settings.modalTitle}
          onChange={(e) => update("modalTitle", e.target.value)}
        />
        <div className="small text-muted mt-1">
          {(settings.modalTitle || "").length}/50 characters
        </div>
      </div>

      {/* ── Widget Mode ───────────────────────────────────── */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark mb-1">
          Widget Mode
        </label>
        <p className="small text-muted mb-2">
          How the widget is displayed on the page.
        </p>
        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={settings.widgetMode}
          onChange={(e) => update("widgetMode", e.target.value)}
        >
          <option value="bubble">Bubble</option>
          <option value="spotlight">Spotlight</option>
        </select>
      </div>

      {/* ── Modal Title Font ──────────────────────────────── */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark mb-1">
          {t("answerEngine.modalTitleFont", "Modal Title Font")}
        </label>
        <p className="small text-muted mb-2">
          {t(
            "answerEngine.modalTitleFontDesc",
            "CSS font-family string for the modal title text.",
          )}
        </p>
        <select
          className="form-select"
          style={{ maxWidth: 300 }}
          value={fontSelectValue}
          onChange={(e) => handleFontSelect(e.target.value)}
        >
          {FONT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {fontSelectValue === "__custom__" && (
          <input
            type="text"
            className="form-control mt-2"
            style={{ maxWidth: 300 }}
            placeholder={`e.g. "Inter, sans-serif"`}
            value={customFont}
            onChange={(e) => handleCustomFontChange(e.target.value)}
          />
        )}
        {fontSelectValue && fontSelectValue !== "__custom__" && (
          <p
            className="mt-2 mb-0 small text-muted"
            style={{ fontFamily: fontSelectValue }}
          >
            {t(
              "answerEngine.fontPreview",
              "Preview: The quick brown fox jumps over the lazy dog",
            )}
          </p>
        )}
      </div>

      {/* ── Welcome Banner ───────────────────────────────── */}
      <div className="mb-4">
        <h6 className="fw-semibold text-dark mb-1">
          {t("answerEngine.welcomeBanner", "Welcome Banner")}
        </h6>
        <p className="small text-muted mb-3">
          {t(
            "answerEngine.welcomeBannerDesc",
            "Configure the assistant welcome banner overrides passed to the widget.",
          )}
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.bannerTitle", "Banner Title")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.bannerTitleDesc",
              "Main headline shown in the widget welcome banner.",
            )}
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 520 }}
            placeholder={t(
              "answerEngine.bannerTitlePlaceholder",
              "How can we help today?",
            )}
            value={settings.bannerTitle}
            onChange={(e) => update("bannerTitle", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.bannerDescription", "Banner Description")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.bannerDescriptionDesc",
              "Supporting text shown below the banner title.",
            )}
          </p>
          <textarea
            className="form-control"
            style={{ maxWidth: 520 }}
            rows={3}
            placeholder={t(
              "answerEngine.bannerDescPlaceholder",
              "Ask a question to get instant, data-backed guidance.",
            )}
            value={settings.bannerDescription}
            onChange={(e) => update("bannerDescription", e.target.value)}
          />
        </div>

        <div className="mb-0">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.bannerImageUrl", "Banner Image URL")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.bannerImageUrlDesc",
              "Image used in the welcome banner and as the chat header icon.",
            )}
          </p>
          <input
            type="url"
            className="form-control"
            style={{ maxWidth: 520 }}
            placeholder="https://example.com/banner-icon.png"
            value={settings.bannerImageUrl}
            onChange={(e) => update("bannerImageUrl", e.target.value)}
          />
        </div>
      </div>

      {/* ── Suggested Questions ───────────────────────────── */}
      <div className="mb-4">
        <label className="form-label fw-semibold text-dark mb-1">
          Suggested Questions
        </label>
        <p className="small text-muted mb-2">
          Pre-filled questions shown to users in the widget.
        </p>
        {(settings.suggestedQuestions || []).map((q, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 460 }}
              placeholder={`Question ${i + 1}`}
              value={q}
              onChange={(e) => {
                const updated = [...(settings.suggestedQuestions || [])];
                updated[i] = e.target.value;
                update("suggestedQuestions", updated);
              }}
            />
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              aria-label={`Remove suggested question ${i + 1}`}
              onClick={() => {
                const updated = (settings.suggestedQuestions || []).filter(
                  (_, idx) => idx !== i,
                );
                update("suggestedQuestions", updated);
              }}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mt-1"
          onClick={() =>
            update("suggestedQuestions", [
              ...(settings.suggestedQuestions || []),
              "",
            ])
          }
        >
          + Add Question
        </button>
      </div>

      {/* ── Glass Effect ──────────────────────────────────── */}
      <div className="mb-4">
        <h6 className="fw-semibold text-dark mb-1">
          {t("answerEngine.glassEffect", "Glass Effect")}
        </h6>
        <p className="small text-muted mb-3">
          {t(
            "answerEngine.glassEffectDesc",
            "Controls the frosted-glass appearance of the widget panels. Leave a field empty to use the widget default.",
          )}
        </p>

        <GlassSlider
          label={t("answerEngine.dialogOpacity", "Dialog Opacity")}
          description={t(
            "answerEngine.dialogOpacityDesc",
            "Opacity of the main dialog panel.",
          )}
          settingKey="glassDialogOpacity"
          min={0}
          max={1}
          step={0.01}
          defaultVal={1}
          value={settings.glassDialogOpacity}
          onChange={update}
        />
        <GlassSlider
          label={t("answerEngine.scrimOpacity", "Scrim Opacity")}
          description={t(
            "answerEngine.scrimOpacityDesc",
            "Opacity of the backdrop scrim behind the dialog.",
          )}
          settingKey="glassScrimOpacity"
          min={0}
          max={1}
          step={0.01}
          defaultVal={0}
          value={settings.glassScrimOpacity}
          onChange={update}
        />
        <GlassSlider
          label={t("answerEngine.blurPx", "Blur (px)")}
          description={t(
            "answerEngine.blurDesc",
            "Background blur radius for the glass panels.",
          )}
          settingKey="glassBlur"
          min={0}
          max={120}
          step={1}
          defaultVal={0}
          value={settings.glassBlur}
          unit="px"
          onChange={update}
        />
        <GlassSlider
          label={t("answerEngine.chatOpacity", "Chat Opacity")}
          description={t(
            "answerEngine.chatOpacityDesc",
            "Opacity of the chat message area.",
          )}
          settingKey="glassChatOpacity"
          min={0}
          max={1}
          step={0.01}
          defaultVal={0}
          value={settings.glassChatOpacity}
          onChange={update}
        />
      </div>

      {/* ── Booking / Calendly ───────────────────────────── */}
      <div className="mb-4">
        <h6 className="fw-semibold text-dark mb-1">
          {t("answerEngine.bookingIntegration", "Booking Integration")}
        </h6>
        <p className="small text-muted mb-3">
          {t(
            "answerEngine.bookingIntegrationDesc",
            "Configure Calendly and call-to-action settings for the widget.",
          )}
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.calendlyUrl", "Calendly URL")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.calendlyUrlDesc",
              "The Calendly scheduling link to open when the booking CTA is clicked.",
            )}
          </p>
          <input
            type="url"
            className="form-control"
            style={{ maxWidth: 380 }}
            placeholder="https://calendly.com/your-link"
            value={settings.calendlyUrl}
            onChange={(e) => update("calendlyUrl", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.bookingCtaTitle", "Booking CTA Title")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.bookingCtaTitleDesc",
              "Heading text displayed above the booking call-to-action.",
            )}
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 380 }}
            placeholder={t(
              "answerEngine.bookingCtaTitlePlaceholder",
              "Schedule a meeting",
            )}
            value={settings.bookingCtaTitle}
            onChange={(e) => update("bookingCtaTitle", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            {t("answerEngine.bookingCtaButton", "Booking CTA Button")}
          </label>
          <p className="small text-muted mb-2">
            {t(
              "answerEngine.bookingCtaButtonDesc",
              "Label shown on the booking call-to-action button.",
            )}
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 380 }}
            placeholder={t(
              "answerEngine.bookingCtaBtnPlaceholder",
              "Book Now",
            )}
            value={settings.bookingCtaButton}
            onChange={(e) => update("bookingCtaButton", e.target.value)}
          />
        </div>
      </div>

      {/* ── HubSpot Integration ───────────────────────────── */}
      <div className="mb-4">
        <h6 className="fw-semibold text-dark mb-1">HubSpot Integration</h6>
        <p className="small text-muted mb-3">
          Connect the widget to a HubSpot form for lead capture.
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            Portal ID
          </label>
          <p className="small text-muted mb-2">
            Your HubSpot portal (account) ID.
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 320 }}
            placeholder="12345678"
            value={settings.hubspotPortalId}
            onChange={(e) => update("hubspotPortalId", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold text-dark mb-1">
            Form ID
          </label>
          <p className="small text-muted mb-2">
            The GUID of the HubSpot form to embed.
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 380 }}
            placeholder="abcd-1234"
            value={settings.hubspotFormId}
            onChange={(e) => update("hubspotFormId", e.target.value)}
          />
        </div>

        <div className="mb-0">
          <label className="form-label fw-semibold text-dark mb-1">
            Region
          </label>
          <p className="small text-muted mb-2">
            HubSpot data residency region (e.g. na1, eu1).
          </p>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 160 }}
            placeholder="na1"
            value={settings.hubspotRegion}
            onChange={(e) => update("hubspotRegion", e.target.value)}
          />
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────── */}
      <div className="d-flex align-items-center gap-2 pt-2">
        <BrandButton
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className="d-flex align-items-center gap-1"
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              Saving…
            </>
          ) : (
            <>
              <Save size={15} />
              {t("answerEngine.saveSettings", "Save Settings")}
            </>
          )}
        </BrandButton>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          onClick={handleReset}
          disabled={isSaving}
        >
          <RotateCcw size={14} />
          {t("answerEngine.resetToDefaults", "Reset to Defaults")}
        </button>
      </div>
      </>)}
    </div>
    </div>

    <div className="col-12 col-md-6">
    <AgentSetting
      aeBackendUrl={AE_BACKEND_URL}
      clientId={clientId}
      isSuperAdmin={isSuperAdmin}
      onDirtyChange={setIsAgentDirty}
    />
    </div>
    </div>
  );
}
